import functions from "common/helper/functions";
import security from "common/managers/security";
import window from "common/objects/window";

// interval between requests to the server to keep session alive in milliseconds
const KEEP_ALIVE_INTERVAL = 30000;

// interval between requests to the server to check on session health in milliseconds
const HEALTH_POLL_INTERVAL = 5000;

// browser events that define user activity
const USER_ACTIVITY_EVENTS = [
    "mousedown",
    "pointerdown",
    "touchstart",
    "mousemove",
    "pointermove",
    "touchmove",
    "keydown",
    "scroll",
    "wheel"
];

const SessionManager = class {
    constructor() {
        this._checkSessionHealth = this._checkSessionHealth.bind(this);
        this._visibilityChanged = this._visibilityChanged.bind(this);
    }

    destroy() {
        this._clean();
    }

    /**
     * Clean up the event handlers and timers so they don't linger
     * @private
     */
    _clean() {
        this._unbindListeners();
        this._clearTimers();
        delete this._expireTime;
    }

    /**
     * Set up the user activity handlers
     * @param {function()} validFn - Callback function called when session is valid and active
     * @param {function()} warnFn - Callback function called when session is about to time out
     * @param {function()} expireFn - Callback function called when session has expired
     * @param {() => PromiseLike} keepAliveFn - Function called to keep the session alive, must return a Thenable
     * @param {() => PromiseLike} sessionHealthFn - Function called to get session health, must return a Thenable
     */
    start(validFn, warnFn, expireFn, keepAliveFn, sessionHealthFn) {
        this._clean();
        this.keepAliveFn = keepAliveFn;
        this.sessionHealthFn = sessionHealthFn;
        this._activityHandler = functions.limit(this._activity, KEEP_ALIVE_INTERVAL, this);
        this._activityHandler();
        this._bindListeners();
        this.validFn = validFn;
        this.warnFn = warnFn;
        this.expireFn = expireFn;
    }

    /**
     * Register activity that keeps the session alive
     */
    activity() {
        this._activityHandler();
    }

    /**
     * Pause the user interaction listeners
     */
    pause() {
        this._stopActivityListener();
    }

    /**
     * Continue listening for user interaction
     */
    resume() {
        this.activity();
        this._startActivityListener();
    }

    /**
     * Terminate the session
     */
    logout() {
        this.destroy();
        window.location.href = `${context.path}/auth/logout/return?url=${encodeURIComponent(window.location.pathname + window.location.search + window.location.hash)}`;
    }

    /**
     * Expire the current session
     * @private
     */
    _expire() {
        this.destroy();
        this.triggerExpire();
    }

    triggerExpire() {
        this.expireFn();
    }

    /**
     * Registers input event listeners to track user activity
     * @private
     */
    _startActivityListener() {
        USER_ACTIVITY_EVENTS.forEach((eventType) => {
            document.body.addEventListener(eventType, this._activityHandler);
        });
    }

    /**
     * Unbinds user input event listeners
     * @private
     */
    _stopActivityListener() {
        USER_ACTIVITY_EVENTS.forEach((eventType) => {
            document.body.removeEventListener(eventType, this._activityHandler);
        });
    }

    /**
     * Listen for tab visibility change to keep separate docfinity tabs in sync
     * @private
     */
    _startVisibilityListener() {
        document.addEventListener("visibilitychange", this._visibilityChanged);
    }

    /**
     * Unbind the tab visibility listener
     * @private
     */
    _stopVisibilityListener() {
        document.removeEventListener("visibilitychange", this._visibilityChanged);
    }

    /**
     * Bind the event listeners
     * @private
     */
    _bindListeners() {
        this._startActivityListener();
        this._startVisibilityListener();
    }

    /**
     * Unbind the element event handlers for user activity detection
     * @private
     */
    _unbindListeners() {
        this._stopActivityListener();
        this._stopVisibilityListener();
    }

    /**
     * Clear the timers so they don't call the handlers
     * @private
     */
    _clearTimers() {
        clearTimeout(this._warnTimer);
        clearTimeout(this._expireTimer);
        clearTimeout(this._healthPoll);
    }

    /**
     * Called when user activity is detected
     * @private
     */
    _activity() {
        // clear the timers so the handlers don't get called before the keep alive returns data
        this._clearTimers();
        this.keepAlive()
            .then((result) => {
                this._updateAuthToken(result.userToken);
                this._syncTimers(this._getDelays(result));
            }, () => this._expire());
    }

    keepAlive() {
        return this.keepAliveFn();
    }

    /**
     * Update the auth token with the latest token from keep alive
     * @param authToken {string} The auth token returned from the server
     * @private
     */
    _updateAuthToken(authToken) {
        security.setAuthToken(authToken);
    }

    /**
     * Handler for when the document visibility changes
     * @private
     */
    _visibilityChanged() {
        if(document.visibilityState === "visible") {
            this._checkSessionHealth();
        }
    }

    /**
     * Calculates the delays for client timers based on the session health response from the server
     * @param sessionHealth {{sessionHealth: string, nowTime: number, warningTime: number, expirationTime: number}} The health of the session.
     * @returns {{warn: number, expire: number}} Delays in milliseconds for the warning and expiration
     * @private
     */
    _getDelays(sessionHealth) {
        return {
            warn: sessionHealth.warningTime - sessionHealth.nowTime,
            expire: sessionHealth.expirationTime - sessionHealth.nowTime
        }
    }

    /**
     * Set a timeout to display the logout warning
     * @param delay {number} Delay in milliseconds when to call the warning callback
     * @private
     */
    _setTimeToWarn(delay) {
        clearTimeout(this._warnTimer);
        this._warnTimer = this._getTimer(this._checkSessionHealth, delay, false);
    }

    /**
     * Set a timeout to logout the session
     * @param delay {number} Delay in milliseconds when to call the expiration callback
     * @private
     */
    _setTimeToExpire(delay) {
        this._expireTime = Date.now() + delay;
        clearTimeout(this._expireTimer);
        this._expireTimer = this._getTimer(this._checkSessionHealth, delay, true);
    }

    /**
     * A polling mechanism to check the session health when the expiration warning has been sent to the user
     * This keeps the warning popups in sync across different tabs
     * @private
     */
    _pollSessionHealth() {
        clearTimeout(this._healthPoll);
        // ensure health poll doesn't cause session health to be called back to back when expiration timeout expires
        if(this._expireTime && this._expireTime - Date.now() > HEALTH_POLL_INTERVAL + 1000) {
            this._healthPoll = this._getTimer(this._checkSessionHealth, HEALTH_POLL_INTERVAL, false);
        }
    }

    /**
     * Sets a timeout to run a function
     * @param callback {function} The function to run when the timer expires
     * @param delay {number} Delay in milliseconds when the callback should be called
     * @param force {boolean} True if the timeout should be created even if the delay is not valid (valid is non-null and greater than 0)
     * @returns {any} The id of the timer that was created or undefined
     * @private
     */
    _getTimer(callback, delay, force) {
        return delay >= 0 || force === true ? setTimeout(callback, Math.max(delay, 0)) : undefined;
    }

    /**
     * Update the times when the session will time out and when the warning should be displayed
     * @param delay {{warn: number, expire: number}} Delays in milliseconds for the warning and expiration
     * @private
     */
    _syncTimers(delay) {
        clearTimeout(this._healthPoll);
        this._setTimeToWarn(delay.warn);
        this._setTimeToExpire(delay.expire);
    }

    /**
     * Checks if session is still inactive in case activity was registered elsewhere
     * @private
     */
    _checkSessionHealth() {
        // ensure session is still inactive (another tab could have activity)
        this.getSessionHealth()
            .then((result) => {
                this._processSessionHealth(result);
            }, () => this._expire());
    }

    getSessionHealth() {
        return this.sessionHealthFn();
    }

    /**
     * Process the data from session health, ensures the session is still valid before it is used
     * @param sessionHealth {{sessionHealth: string, nowTime: number, warningTime: number, expirationTime: number}} The health of the session.
     * @private
     */
    _processSessionHealth(sessionHealth) {
        if(sessionHealth.sessionHealth === "VALID") {
            // need to resync timers since activity for this session was detected elsewhere
            this._syncTimers(this._getDelays(sessionHealth));
            this.triggerValid();
        } else if(sessionHealth.sessionHealth === "WARN") {
            this.triggerWarn();
            this._pollSessionHealth();
        } else {
            this._expire();
        }
    }

    triggerValid() {
        this.validFn();
    }

    triggerWarn() {
        this.warnFn();
    }
};

export default new SessionManager();
